import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import thumbsUpIcon from "../../assets/icons/thumbs-up-green.svg";
import warningTriangleIcon from "../../assets/icons/warning-triangle.svg";
import { buildWarningMessageList } from "../../utils/warnings";

// Helper to join React nodes with commas and "and"
const joinNodesWithCommasAnd = (nodes) => {
  if (!nodes || nodes.length === 0) return null;
  if (nodes.length === 1) return nodes[0];
  if (nodes.length === 2)
    return (
      <>
        {nodes[0]} and {nodes[1]}
      </>
    );
  const allButLast = nodes
    .slice(0, -1)
    .map((n, i) => <React.Fragment key={i}>{n}, </React.Fragment>);
  const last = nodes[nodes.length - 1];
  return (
    <>
      {allButLast}and {last}
    </>
  );
};

/**
 * @param {Object}  props
 * @param {Object}  props.assessment  the whole assessment — reads
 *   all_warning_list (the W001-W027 pipeline messages), the same source and
 *   the only source the case list and the current design's own CaptureIssues
 *   (src/components/CaptureIssues.js) read, so a case's warning count agrees
 *   everywhere it's shown rather than drifting between pages.
 * @param {boolean} props.isLoading
 * @param {"legacy"|"clearline"} [props.variant]
 *   "clearline" renders the amber "▲ N warnings" pill from the redesign and
 *   skips the auto-opening modal — the pill itself is the affordance there.
 */
const CaptureIssues = ({ assessment, isLoading, variant = "legacy" }) => {
  const [issues, setIssues] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isClearline = variant === "clearline";

  useEffect(() => {
    const newIssues = buildWarningMessageList(assessment);
    setIssues(newIssues);
    if (newIssues.length > 0 && !isClearline) setIsModalOpen(true);
  }, [assessment, isClearline]);

  return (
    <>
      {!isLoading &&
        (isClearline ? (
          issues.length > 0 && (
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center gap-[6px] whitespace-nowrap rounded-pill bg-[#fdf3df] px-[11px] py-1 text-[11.5px] font-semibold text-[#8a5c07] transition-opacity hover:opacity-85"
            >
              <AlertTriangle className="h-[11px] w-[11px]" />
              {issues.length} warning{issues.length > 1 ? "s" : ""}
            </button>
          )
        ) : issues.length > 0 ? (
          <div
            onClick={() => setIsModalOpen(true)}
            className="cursor-pointer flex items-center gap-1.5 text-xs font-semibold text-red-600 whitespace-nowrap"
          >
            <img alt="Warning" className="w-5 h-5" src={warningTriangleIcon} />
            {issues.length} Capture Issue{issues.length > 1 ? "s" : ""}
          </div>
        ) : (
          <div className="cursor-default flex items-center gap-1.5 text-xs font-semibold text-green-600 whitespace-nowrap">
            <img src={thumbsUpIcon} alt="thumbs-up" />
            No Capture Issues
          </div>
        ))}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[14%] bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-[550px] overflow-visible border border-gray-200 p-2 relative">
            <div className="text-end">
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-4 h-4 text-zinc-800 font-semibold" />
              </button>
            </div>

            <div className="flex px-5 pt-3 items-center gap-1 pb-3 text-red-600">
              <img
                alt="Warning"
                className="w-5 h-5"
                src={warningTriangleIcon}
              />
              <h3 className="text-sm">
                Capture Issues <span>• {issues.length}</span>
              </h3>
            </div>

            <div className="px-5 pb-8 relative overflow-visible">
              <ol className="list-decimal pl-5 text-left space-y-2 relative overflow-visible">
                {issues.map((issue, i) => (
                  <li key={i} className="text-sm text-gray-700 relative">
                    <span>{issue.message}</span>{" "}
                    {issue.sidesData &&
                      joinNodesWithCommasAnd(
                        issue.sidesData.map((side) => (
                          <span
                            key={side.name}
                            className="relative cursor-pointer text-blue-600 hover:text-blue-700 group"
                          >
                            {side.name}
                            {side.imageUrl && (
                              <div className="absolute left-1/2 -translate-x-1/2 top-6 hidden group-hover:block z-[9999] bg-white border border-gray-200 shadow-2xl rounded-lg p-1 w-[260px]">
                                <img
                                  src={side.imageUrl}
                                  alt={side.name}
                                  className="rounded-md w-full h-auto object-contain"
                                />
                              </div>
                            )}
                          </span>
                        )),
                      )}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CaptureIssues;
